import type { MarketingTranslator } from '@/i18n/marketing'
/**
 * Content for the LC About page (`/about/lc`).
 *
 * Each section's structure mirrors the Figma desktop (57:5301) + mobile (186:3634).
 * Image URLs point to:
 *   - local drop dir: `/marketing/about-lc/*` (apps/lc/public/marketing/about-lc/)
 *   - Legacy CloudFront for #03 video/workbook/game (re-used from product-2510)
 */

const IMG = '/marketing/about-lc'
const S3 = 'https://d2ql3aoebfyj3b.cloudfront.net'

export interface ContentTabSlide {
  url: string
  /** HLS manifest for video tab (Wave 8 will wire actual playback) */
  videoUrl?: string
}

export interface ContentTab {
  value: string
  label: string
  /** small line above the big subline */
  panelTitle: string
  /** big bold statement under panelTitle */
  panelSubline: string
  slides: ContentTabSlide[]
  /** show play overlay on each slide (only true for video tab) */
  hasPlayOverlay?: boolean
}

export type CourseColorTheme = 'green' | 'yellow' | 'blue' | 'purple' | 'red'

export interface CourseLevel {
  course: string
  /** Title line 1 (e.g. "수학의 완전한") */
  titleA: string
  /** Title line 2 (e.g. "첫 시작") */
  titleB: string
  lessons: string
  duration: string
  patternUrl: string
  colorTheme: CourseColorTheme
}

export interface LearningStepTab {
  value: string
  step: string
  label: string
  bullets: string[]
  imageUrl: string
}

export interface TestInfoRow {
  label: string
  value: string
}

export interface OutcomeCard {
  /** First phrase shown in primary blue */
  titlePrimary: string
  /** Remaining phrase shown in high black */
  titleHigh: string
  description: string
  imageUrl: string
  imageUrlMobile: string
}

export function getAboutLcContent(mt: MarketingTranslator) {
  const aboutLcContent = {
    // #01 Hero
    hero: {
      label: 'LEARNING CENTER',
      bodyLine1: mt('aboutLc.aboutLcContent.hero.bodyLine1'),
      bodyLine2: mt('aboutLc.aboutLcContent.hero.bodyLine2'),
      emphasis: mt('aboutLc.aboutLcContent.hero.emphasis'),
      ctaLabel: mt('aboutLc.aboutLcContent.hero.ctaLabel'),
      imageUrl: `${IMG}/lc.webp`,
      imageUrlMobile: `${IMG}/lc-m.webp`,
      imageAlt: mt('aboutLc.aboutLcContent.hero.imageAlt'),
    },

    // #02 Self-directed learning
    selfDirected: {
      /** Title (primary blue) split for mobile linebreak; desktop joins with space */
      titlePrimaryA: mt('aboutLc.aboutLcContent.selfDirected.titlePrimaryA'),
      titlePrimaryB: mt('aboutLc.aboutLcContent.selfDirected.titlePrimaryB'),
      /** Title line 2 (high black) split similarly */
      titleHighA: mt('aboutLc.aboutLcContent.selfDirected.titleHighA'),
      titleHighB: mt('aboutLc.aboutLcContent.selfDirected.titleHighB'),
      body: mt('aboutLc.aboutLcContent.selfDirected.body'),
      emphasis: mt('aboutLc.aboutLcContent.selfDirected.emphasis'),
      imageUrl: `${IMG}/product.webp`,
      imageUrlMobile: `${IMG}/product-m.webp`,
      imageAlt: mt('aboutLc.aboutLcContent.selfDirected.imageAlt'),
    },

    // #03 Content tabs
    contentTabs: {
      /** Desktop title line 1 (high black) */
      titleHighDesktop: mt('aboutLc.aboutLcContent.contentTabs.titleHighDesktop'),
      /** Desktop title line 2 (primary blue) */
      titlePrimaryDesktop: mt('aboutLc.aboutLcContent.contentTabs.titlePrimaryDesktop'),
      /** Mobile title line 1 (high black) split */
      titleHighMobileA: mt('aboutLc.aboutLcContent.contentTabs.titleHighMobileA'),
      titleHighMobileB: mt('aboutLc.aboutLcContent.contentTabs.titleHighMobileB'),
      /** Mobile title line 2 (primary blue) split — note different wording on mobile */
      titlePrimaryMobileA: mt('aboutLc.aboutLcContent.contentTabs.titlePrimaryMobileA'),
      titlePrimaryMobileB: mt('aboutLc.aboutLcContent.contentTabs.titlePrimaryMobileB'),
      tabs: [
        {
          value: 'video',
          label: mt('aboutLc.aboutLcContent.contentTabs.tabs.0.label'),
          panelTitle: mt('aboutLc.aboutLcContent.contentTabs.tabs.0.panelTitle'),
          panelSubline: mt('aboutLc.aboutLcContent.contentTabs.tabs.0.panelSubline'),
          hasPlayOverlay: true,
          slides: [
            {
              url: `${S3}/freetrial/content-section/J_13360711805315063.jpg`,
              videoUrl:
                'https://quebon-public-media.s3.ap-northeast-2.amazonaws.com/J_13360711805315063.mp4/stream.m3u8',
            },
            {
              url: `${S3}/freetrial/content-section/P_1430939004602016.jpg`,
              videoUrl:
                'https://quebon-public-media.s3.ap-northeast-2.amazonaws.com/P_1430939004602016.mp4/stream.m3u8',
            },
            {
              url: `${S3}/freetrial/content-section/P2_18496965072815381.jpg`,
              videoUrl:
                'https://quebon-public-media.s3.ap-northeast-2.amazonaws.com/P2_18496965072815381.mp4/stream.m3u8',
            },
          ],
        },
        {
          value: 'checkup',
          label: mt('aboutLc.aboutLcContent.contentTabs.tabs.1.label'),
          panelTitle: mt('aboutLc.aboutLcContent.contentTabs.tabs.1.panelTitle'),
          panelSubline: mt('aboutLc.aboutLcContent.contentTabs.tabs.1.panelSubline'),
          slides: [
            { url: `${IMG}/03-img-checkupbook01.webp` },
            { url: `${IMG}/03-img-checkupbook02.webp` },
            { url: `${IMG}/03-img-checkupbook03.webp` },
          ],
        },
        {
          value: 'workbook',
          label: mt('aboutLc.aboutLcContent.contentTabs.tabs.2.label'),
          panelTitle: mt('aboutLc.aboutLcContent.contentTabs.tabs.2.panelTitle'),
          panelSubline: mt('aboutLc.aboutLcContent.contentTabs.tabs.2.panelSubline'),
          slides: [
            { url: `${S3}/product-2510/online-section-04/img-workbook1.png` },
            { url: `${S3}/product-2510/online-section-04/img-workbook2.png` },
            { url: `${S3}/product-2510/online-section-04/img-workbook3.png` },
          ],
        },
        {
          value: 'game',
          label: mt('aboutLc.aboutLcContent.contentTabs.tabs.3.label'),
          panelTitle: mt('aboutLc.aboutLcContent.contentTabs.tabs.3.panelTitle'),
          panelSubline: mt('aboutLc.aboutLcContent.contentTabs.tabs.3.panelSubline'),
          slides: [
            { url: `${S3}/freetrial/content-section/03-img-game01.png` },
            { url: `${S3}/freetrial/content-section/03-img-game02.png` },
            { url: `${S3}/freetrial/content-section/03-img-game03.png` },
            { url: `${S3}/freetrial/content-section/03-img-game04.png` },
            { url: `${S3}/freetrial/content-section/03-img-game05.png` },
          ],
        },
      ] satisfies ContentTab[],
    },

    // #04 Diagnosis (image based)
    diagnosis: {
      /** Title line 1 (high black) */
      titleHigh: mt('aboutLc.aboutLcContent.diagnosis.titleHigh'),
      /** Title line 2 (primary blue) — desktop one line */
      titlePrimary: mt('aboutLc.aboutLcContent.diagnosis.titlePrimary'),
      /** Mobile-specific split for line 2 */
      titlePrimaryMobileA: mt('aboutLc.aboutLcContent.diagnosis.titlePrimaryMobileA'),
      titlePrimaryMobileB: mt('aboutLc.aboutLcContent.diagnosis.titlePrimaryMobileB'),
      note: mt('aboutLc.aboutLcContent.diagnosis.note'),
      imageUrl: `${IMG}/contents.webp`,
      imageUrlMobile: `${IMG}/contents-m.webp`,
      imageAlt:
        mt('aboutLc.aboutLcContent.diagnosis.imageAlt'),
    },

    // #05 Course levels A~E
    courses: {
      /** Title line 1 (high) */
      titleHigh: mt('aboutLc.aboutLcContent.courses.titleHigh'),
      /** Title line 2 (primary) */
      titlePrimary: mt('aboutLc.aboutLcContent.courses.titlePrimary'),
      /** Mobile-specific splits (high) */
      titleHighMobileA: mt('aboutLc.aboutLcContent.courses.titleHighMobileA'),
      titleHighMobileB: mt('aboutLc.aboutLcContent.courses.titleHighMobileB'),
      /** Mobile-specific splits (primary) */
      titlePrimaryMobileA: mt('aboutLc.aboutLcContent.courses.titlePrimaryMobileA'),
      titlePrimaryMobileB: mt('aboutLc.aboutLcContent.courses.titlePrimaryMobileB'),
      /** Description with rich middle emphasis */
      descPart1: mt('aboutLc.aboutLcContent.courses.descPart1'),
      descEmphasis: mt('aboutLc.aboutLcContent.courses.descEmphasis'),
      descPart2:
        mt('aboutLc.aboutLcContent.courses.descPart2'),
      levels: [
        {
          course: 'A',
          titleA: mt('aboutLc.aboutLcContent.courses.levels.0.titleA'),
          titleB: mt('aboutLc.aboutLcContent.courses.levels.0.titleB'),
          lessons: mt('aboutLc.aboutLcContent.courses.levels.0.lessons'),
          duration: mt('aboutLc.aboutLcContent.courses.levels.0.duration'),
          patternUrl: `${IMG}/patternA.svg`,
          colorTheme: 'green',
        },
        {
          course: 'B',
          titleA: mt('aboutLc.aboutLcContent.courses.levels.1.titleA'),
          titleB: mt('aboutLc.aboutLcContent.courses.levels.1.titleB'),
          lessons: mt('aboutLc.aboutLcContent.courses.levels.1.lessons'),
          duration: mt('aboutLc.aboutLcContent.courses.levels.1.duration'),
          patternUrl: `${IMG}/patternB.svg`,
          colorTheme: 'yellow',
        },
        {
          course: 'C',
          titleA: mt('aboutLc.aboutLcContent.courses.levels.2.titleA'),
          titleB: mt('aboutLc.aboutLcContent.courses.levels.2.titleB'),
          lessons: mt('aboutLc.aboutLcContent.courses.levels.2.lessons'),
          duration: mt('aboutLc.aboutLcContent.courses.levels.2.duration'),
          patternUrl: `${IMG}/patternC.svg`,
          colorTheme: 'blue',
        },
        {
          course: 'D',
          titleA: mt('aboutLc.aboutLcContent.courses.levels.3.titleA'),
          titleB: mt('aboutLc.aboutLcContent.courses.levels.3.titleB'),
          lessons: mt('aboutLc.aboutLcContent.courses.levels.3.lessons'),
          duration: mt('aboutLc.aboutLcContent.courses.levels.3.duration'),
          patternUrl: `${IMG}/patternD.svg`,
          colorTheme: 'purple',
        },
        {
          course: 'E',
          titleA: mt('aboutLc.aboutLcContent.courses.levels.4.titleA'),
          titleB: mt('aboutLc.aboutLcContent.courses.levels.4.titleB'),
          lessons: mt('aboutLc.aboutLcContent.courses.levels.4.lessons'),
          duration: mt('aboutLc.aboutLcContent.courses.levels.4.duration'),
          patternUrl: `${IMG}/patternE.webp`,
          colorTheme: 'red',
        },
      ] satisfies CourseLevel[],
    },

    // #06 System title
    systemTitle: {
      line1High: mt('aboutLc.aboutLcContent.systemTitle.line1High'),
      line2Primary: mt('aboutLc.aboutLcContent.systemTitle.line2Primary'),
      /** Mobile splits */
      line1HighMobileA: mt('aboutLc.aboutLcContent.systemTitle.line1HighMobileA'),
      line1HighMobileB: mt('aboutLc.aboutLcContent.systemTitle.line1HighMobileB'),
      line2PrimaryMobileA: mt('aboutLc.aboutLcContent.systemTitle.line2PrimaryMobileA'),
      line2PrimaryMobileB: mt('aboutLc.aboutLcContent.systemTitle.line2PrimaryMobileB'),
    },

    // #07 Today learning — 5 step tabs
    todayLearning: {
      badge: '1',
      title: mt('aboutLc.aboutLcContent.todayLearning.title'),
      description: mt('aboutLc.aboutLcContent.todayLearning.description'),
      note: mt('aboutLc.aboutLcContent.todayLearning.note'),
      tabs: [
        {
          value: 'step1',
          step: 'Step 1',
          label: mt('aboutLc.aboutLcContent.todayLearning.tabs.0.label'),
          bullets: [
            mt('aboutLc.aboutLcContent.todayLearning.tabs.0.bullets.0'),
            mt('aboutLc.aboutLcContent.todayLearning.tabs.0.bullets.1'),
            mt('aboutLc.aboutLcContent.todayLearning.tabs.0.bullets.2'),
          ],
          imageUrl: `${IMG}/step-01.webp`,
        },
        {
          value: 'step2',
          step: 'Step 2',
          label: mt('aboutLc.aboutLcContent.todayLearning.tabs.1.label'),
          bullets: [
            mt('aboutLc.aboutLcContent.todayLearning.tabs.1.bullets.0'),
            mt('aboutLc.aboutLcContent.todayLearning.tabs.1.bullets.1'),
            mt('aboutLc.aboutLcContent.todayLearning.tabs.1.bullets.2'),
          ],
          imageUrl: `${IMG}/step-02.webp`,
        },
        {
          value: 'step3',
          step: 'Step 3',
          label: mt('aboutLc.aboutLcContent.todayLearning.tabs.2.label'),
          bullets: [
            mt('aboutLc.aboutLcContent.todayLearning.tabs.2.bullets.0'),
            mt('aboutLc.aboutLcContent.todayLearning.tabs.2.bullets.1'),
            mt('aboutLc.aboutLcContent.todayLearning.tabs.2.bullets.2'),
          ],
          imageUrl: `${IMG}/step-03.webp`,
        },
        {
          value: 'step4',
          step: 'Step 4',
          label: mt('aboutLc.aboutLcContent.todayLearning.tabs.3.label'),
          bullets: [
            mt('aboutLc.aboutLcContent.todayLearning.tabs.3.bullets.0'),
            mt('aboutLc.aboutLcContent.todayLearning.tabs.3.bullets.1'),
            mt('aboutLc.aboutLcContent.todayLearning.tabs.3.bullets.2'),
          ],
          imageUrl: `${IMG}/step-04.webp`,
        },
        {
          value: 'step5',
          step: 'Step 5',
          label: mt('aboutLc.aboutLcContent.todayLearning.tabs.4.label'),
          bullets: [
            mt('aboutLc.aboutLcContent.todayLearning.tabs.4.bullets.0'),
            mt('aboutLc.aboutLcContent.todayLearning.tabs.4.bullets.1'),
            mt('aboutLc.aboutLcContent.todayLearning.tabs.4.bullets.2'),
          ],
          imageUrl: `${IMG}/step-05.webp`,
        },
      ] satisfies LearningStepTab[],
    },

    // #08 Chapter test
    chapterTest: {
      badge: '2',
      title: mt('aboutLc.aboutLcContent.chapterTest.title'),
      description: mt('aboutLc.aboutLcContent.chapterTest.description'),
      panelTitle: mt('aboutLc.aboutLcContent.chapterTest.title'),
      rows: [
        { label: mt('aboutLc.aboutLcContent.chapterTest.rows.0.label'), value: mt('aboutLc.aboutLcContent.chapterTest.rows.0.value') },
        { label: mt('aboutLc.aboutLcContent.chapterTest.rows.1.label'), value: mt('aboutLc.aboutLcContent.chapterTest.rows.1.value') },
        { label: mt('aboutLc.aboutLcContent.chapterTest.rows.2.label'), value: mt('aboutLc.aboutLcContent.chapterTest.rows.2.value') },
        { label: mt('aboutLc.aboutLcContent.chapterTest.rows.3.label'), value: mt('aboutLc.aboutLcContent.chapterTest.rows.3.value') },
        { label: mt('aboutLc.aboutLcContent.chapterTest.rows.4.label'), value: mt('aboutLc.aboutLcContent.chapterTest.rows.4.value') },
      ] satisfies TestInfoRow[],
      imageUrl: `${IMG}/chapter-test.webp`,
      imageUrlMobile: `${IMG}/chapter-test-m.webp`,
      imageAlt: mt('aboutLc.aboutLcContent.chapterTest.imageAlt'),
    },

    // #09 Cumulative test
    cumulativeTest: {
      badge: '3',
      title: mt('aboutLc.aboutLcContent.cumulativeTest.title'),
      description: mt('aboutLc.aboutLcContent.cumulativeTest.description'),
      panelTitle: mt('aboutLc.aboutLcContent.cumulativeTest.title'),
      rows: [
        { label: mt('aboutLc.aboutLcContent.chapterTest.rows.0.label'), value: mt('aboutLc.aboutLcContent.cumulativeTest.rows.0.value') },
        { label: mt('aboutLc.aboutLcContent.chapterTest.rows.1.label'), value: mt('aboutLc.aboutLcContent.cumulativeTest.rows.1.value') },
        { label: mt('aboutLc.aboutLcContent.chapterTest.rows.2.label'), value: mt('aboutLc.aboutLcContent.chapterTest.rows.2.value') },
        { label: mt('aboutLc.aboutLcContent.chapterTest.rows.3.label'), value: mt('aboutLc.aboutLcContent.chapterTest.rows.3.value') },
        { label: mt('aboutLc.aboutLcContent.chapterTest.rows.4.label'), value: mt('aboutLc.aboutLcContent.chapterTest.rows.4.value') },
      ] satisfies TestInfoRow[],
      imageUrl: `${IMG}/cumulative-test.webp`,
      imageUrlMobile: `${IMG}/cumulative-test-m.webp`,
      imageAlt: mt('aboutLc.aboutLcContent.cumulativeTest.imageAlt'),
    },

    // #10 Outcome cards (3)
    outcomes: {
      titleHigh: mt('aboutLc.aboutLcContent.outcomes.titleHigh'),
      titlePrimary: mt('aboutLc.aboutLcContent.outcomes.titlePrimary'),
      /** Mobile splits */
      titleHighMobileA: mt('aboutLc.aboutLcContent.outcomes.titleHighMobileA'),
      titleHighMobileB: mt('aboutLc.aboutLcContent.outcomes.titleHighMobileB'),
      titlePrimaryMobileA: mt('aboutLc.aboutLcContent.outcomes.titlePrimaryMobileA'),
      titlePrimaryMobileB: mt('aboutLc.aboutLcContent.outcomes.titlePrimaryMobileB'),
      cards: [
        {
          titlePrimary: mt('aboutLc.aboutLcContent.outcomes.cards.0.titlePrimary'),
          titleHigh: mt('aboutLc.aboutLcContent.outcomes.cards.0.titleHigh'),
          description:
            mt('aboutLc.aboutLcContent.outcomes.cards.0.description'),
          imageUrl: `${IMG}/outcome-01.webp`,
          imageUrlMobile: `${IMG}/outcome-01-m.webp`,
        },
        {
          titlePrimary: mt('aboutLc.aboutLcContent.outcomes.cards.1.titlePrimary'),
          titleHigh: mt('aboutLc.aboutLcContent.outcomes.cards.1.titleHigh'),
          description:
            mt('aboutLc.aboutLcContent.outcomes.cards.1.description'),
          imageUrl: `${IMG}/outcome-02.webp`,
          imageUrlMobile: `${IMG}/outcome-02-m.webp`,
        },
        {
          titlePrimary: mt('aboutLc.aboutLcContent.outcomes.cards.2.titlePrimary'),
          titleHigh: mt('aboutLc.aboutLcContent.outcomes.cards.2.titleHigh'),
          description:
            mt('aboutLc.aboutLcContent.outcomes.cards.2.description'),
          imageUrl: `${IMG}/outcome-03.webp`,
          imageUrlMobile: `${IMG}/outcome-03-m.webp`,
        },
      ] satisfies OutcomeCard[],
    },

    // #11 Unit-special videos
    unitSpecial: {
      /** Title line 1 — primary blue (top) */
      titlePrimary: mt('aboutLc.aboutLcContent.unitSpecial.titlePrimary'),
      /** Title line 2 — high black (bottom) */
      titleHigh: mt('aboutLc.aboutLcContent.unitSpecial.titleHigh'),
      description: mt('aboutLc.aboutLcContent.unitSpecial.description'),
      descriptionMobileA: mt('aboutLc.aboutLcContent.unitSpecial.descriptionMobileA'),
      descriptionMobileB: mt('aboutLc.aboutLcContent.unitSpecial.descriptionMobileB'),
      note: mt('aboutLc.aboutLcContent.unitSpecial.note'),
      slides: [
        {
          url: `${IMG}/video-tutoring-01.webp`,
          video: 'https://quebon-public-media.s3.ap-northeast-2.amazonaws.com/unit-test-01/unit-test-01.m3u8',
        },
        {
          url: `${IMG}/video-tutoring-02.webp`,
          video: 'https://quebon-public-media.s3.ap-northeast-2.amazonaws.com/unit-test-02/unit-test-02.m3u8',
        },
        {
          url: `${IMG}/video-tutoring-03.webp`,
          video: 'https://quebon-public-media.s3.ap-northeast-2.amazonaws.com/unit-test-03/unit-test-03.m3u8',
        },
      ],
    },
  }
  return aboutLcContent
}
